import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SafeUrl } from "@angular/platform-browser";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ClipboardService } from "../clipboard.service";
import { DigitalCardService } from "../digital-card.service";
import {
  SHARE_QR,
  SHARE_QR_PNG_PX,
  SHARE_QR_PREVIEW_PX,
  SHARE_QR_SVG_PX,
} from "../../shared/qr-render";
import {
  DigitalCardShareDialogData,
  SHARE_MEDIUMS,
  SourceMedium,
} from "../digital-cards.interface";

@Component({
  selector: "app-digital-card-share-dialog",
  templateUrl: "./digital-card-share-dialog.component.html",
  styleUrls: ["./digital-card-share-dialog.component.scss"],
  standalone: false,
})
export class DigitalCardShareDialogComponent implements OnDestroy {
  readonly shareQr = SHARE_QR;
  readonly qrSvgPx = SHARE_QR_SVG_PX;
  readonly qrPngPx = SHARE_QR_PNG_PX;
  readonly qrPreviewPx = SHARE_QR_PREVIEW_PX;
  qrSvgDownloadLink: SafeUrl | null = null;
  qrPngDownloadLink: SafeUrl | null = null;
  qrSourceUrl = "";
  entryUrl = "";
  sourceLinksReady = false;
  private readonly sourceUrlCache = new Map<SourceMedium, string>();
  private readonly destroy$ = new Subject<void>();

  @ViewChild("downloadQrSvgRef")
  downloadQrSvgRef?: ElementRef<HTMLAnchorElement>;
  @ViewChild("downloadQrPngRef")
  downloadQrPngRef?: ElementRef<HTMLAnchorElement>;

  constructor(
    public dialogRef: MatDialogRef<DigitalCardShareDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DigitalCardShareDialogData,
    private clipboard: ClipboardService,
    private digitalCardService: DigitalCardService,
  ) {
    this.loadSourceTokens();
    this.loadEntryLink();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get shareUrl(): string {
    return this.data.shareUrl?.trim() ?? "";
  }

  get displayName(): string {
    return this.data.displayName?.trim() ?? "";
  }

  get email(): string {
    return this.data.email?.trim() ?? "";
  }

  get profileId(): string {
    return this.data.profileId?.trim() ?? "";
  }

  get qrFileBase(): string {
    const name = (this.displayName || "")
      .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    const mail = (this.email || "")
      .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);
    if (name && mail) {
      return `qr-${name}-${mail}`;
    }
    if (name) {
      return `qr-${name}`;
    }
    if (mail) {
      return `qr-${mail}`;
    }
    return "qr";
  }

  close(): void {
    this.dialogRef.close();
  }

  copyLink(): void {
    const url = this.sourceUrlCache.get("share") ?? "";
    if (!url) {
      return;
    }
    this.clipboard.copy(url, "digitalCard.owner.linkCopied");
  }

  copySourceLink(medium: SourceMedium): void {
    const url = this.sourceUrlCache.get(medium) ?? "";
    if (!url) {
      return;
    }
    this.clipboard.copy(url, "digitalCard.owner.linkCopied");
  }

  copyEntryLink(): void {
    if (!this.entryUrl) {
      return;
    }
    this.clipboard.copy(this.entryUrl, "digitalCard.owner.linkCopied");
  }

  openShare(type: "whatsapp" | "facebook" | "x" | "linkedin" | "email"): void {
    const link =
      type === "email"
        ? (this.sourceUrlCache.get("email") ?? "")
        : type === "whatsapp"
          ? (this.sourceUrlCache.get("whatsapp") ?? "")
          : type === "facebook"
            ? (this.sourceUrlCache.get("facebook") ?? "")
            : type === "x"
              ? (this.sourceUrlCache.get("x") ?? "")
              : (this.sourceUrlCache.get("linkedin") ?? "");
    if (!link) {
      return;
    }
    const subject = encodeURIComponent(
      this.displayName ? `${this.displayName} - Contact card` : "Contact card",
    );
    if (type === "email") {
      window.location.href = `mailto:?subject=${subject}&body=${encodeURIComponent(link)}`;
      return;
    }
    if (type === "whatsapp") {
      window.open(
        `https://wa.me/?text=${encodeURIComponent(link)}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (type === "facebook") {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (type === "x") {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(link)}&text=${subject}`,
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(link)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  downloadQrSvg(): void {
    this.downloadQrSvgRef?.nativeElement?.click();
  }

  downloadQrPng(): void {
    this.downloadQrPngRef?.nativeElement?.click();
  }

  private loadSourceTokens(): void {
    if (!this.profileId) {
      this.sourceLinksReady = true;
      return;
    }
    this.digitalCardService
      .getSourceTokens(this.profileId, [...SHARE_MEDIUMS])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          for (const medium of SHARE_MEDIUMS) {
            const sourceUrl = res[medium]?.sourceUrl?.trim() ?? "";
            if (!sourceUrl) {
              continue;
            }
            this.sourceUrlCache.set(medium, sourceUrl);
            if (medium === "qr") {
              this.qrSourceUrl = sourceUrl;
            }
          }
          this.sourceLinksReady = true;
        },
        error: () => {
          this.sourceLinksReady = true;
        },
      });
  }

  private loadEntryLink(): void {
    if (!this.profileId) {
      return;
    }
    this.digitalCardService
      .getEntryLink(this.profileId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.entryUrl = res?.entryUrl?.trim() ?? "";
        },
      });
  }
}
